import { useState, useEffect, useRef, useCallback } from "react";
import { X, Phone } from "lucide-react";
import { useLocation } from "wouter";

const STORAGE_KEY = "parusa_exit_intent_shown";
const MIN_TIME_ON_PAGE_MS = 8000;

const base = import.meta.env.BASE_URL.replace(/\/$/, "");

export function ExitIntentModal() {
  const [visible, setVisible] = useState(false);
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [location] = useLocation();
  const shownRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pageOpenedAt = useRef(Date.now());

  const isAdmin = location.startsWith("/admin");

  const tryShow = useCallback(() => {
    if (shownRef.current) return;
    if (isAdmin) return;
    if (Date.now() - pageOpenedAt.current < MIN_TIME_ON_PAGE_MS) return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    shownRef.current = true;
    sessionStorage.setItem(STORAGE_KEY, "1");
    setVisible(true);
    if (typeof (window as any).ym === "function") {
      (window as any).ym(109746755, "reachGoal", "exit_intent_shown");
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) return;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 5) tryShow();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") tryShow();
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    timerRef.current = setTimeout(tryShow, 45000);

    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAdmin, tryShow]);

  useEffect(() => {
    pageOpenedAt.current = Date.now();
  }, [location]);

  const close = () => {
    setVisible(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = phone.trim();
    if (!trimmed) return;
    setStatus("loading");
    try {
      const res = await fetch(`${base}/api/callback/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: trimmed, page: location, source: "exit_intent" }),
      });
      if (!res.ok) throw new Error("error");
      setStatus("success");
      if (typeof (window as any).ym === "function") {
        (window as any).ym(109746755, "reachGoal", "exit_intent_submit");
      }
      setTimeout(() => setVisible(false), 3000);
    } catch {
      setStatus("error");
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[195] flex items-center justify-center px-4"
      onClick={close}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md bg-card border border-primary/25 p-8 md:p-10 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "slideUpFade 0.35s ease both" }}
      >
        <button
          onClick={close}
          aria-label="Закрыть"
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {status === "success" ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 rounded-full border border-accent flex items-center justify-center mx-auto mb-5">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-accent">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h3 className="font-serif text-2xl font-light text-foreground mb-3">Спасибо!</h3>
            <p className="text-muted-foreground text-sm font-light leading-relaxed">
              Мы перезвоним вам в течение&nbsp;15&nbsp;минут.
            </p>
          </div>
        ) : (
          <>
            <div className="inline-block px-5 py-1.5 border border-primary/30 mb-6">
              <span className="text-[9px] tracking-[0.35em] uppercase text-accent">Уходите?</span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-light text-foreground mb-3 leading-tight">
              Оставьте телефон —<br />
              <span className="text-accent italic">перезвоним за 15 мин</span>
            </h2>
            <p className="text-muted-foreground text-sm font-light leading-relaxed mb-8">
              Подберём столик или ответим на любой вопрос о&nbsp;ресторане и&nbsp;банкетах.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-primary/50" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+7 (___) ___-__-__"
                  required
                  autoFocus
                  className="w-full bg-background border border-primary/25 focus:border-primary/60 outline-none px-4 pl-11 py-3.5 text-sm text-foreground placeholder:text-muted-foreground/50 tracking-wider transition-colors"
                />
              </div>
              {status === "error" && (
                <p className="text-red-400 text-xs">Ошибка. Попробуйте ещё раз или позвоните нам.</p>
              )}
              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full bg-primary text-background py-3.5 text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-accent transition-colors disabled:opacity-60"
              >
                {status === "loading" ? "Отправляем..." : "Перезвонить мне"}
              </button>
            </form>

            <p className="text-muted-foreground/40 text-[10px] text-center mt-5 tracking-wide">
              Никакого спама — только один звонок
            </p>
          </>
        )}
      </div>

      <style>{`
        @keyframes slideUpFade {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
